<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">My Attendance Records</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Attendance Records</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="row align-items-end">
                    <div class="col-md-3">
                      <label class="form-label">Start Date</label>
                      <input v-model="filters.startDate" type="date" class="form-control">
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">End Date</label>
                      <input v-model="filters.endDate" type="date" class="form-control" :max="today">
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Filter by Status</label>
                      <select v-model="filters.status" class="form-select">
                        <option value="">All Status</option>
                        <option value="Present">Present</option>
                        <option value="Late">Late</option>
                        <option value="Partial">Partial</option>
                        <option value="Absent">Absent</option>
                        <option value="Overtime">Overtime</option>
                      </select>
                    </div>
                    <div class="col-md-3 mt-3 mt-md-0">
                      <button @click="loadAttendanceRecords" class="btn btn-primary w-100">
                        <i class="mdi mdi-filter me-1"></i>
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading attendance records...</p>
          </div>

          <div v-else-if="attendanceData">
            <!-- Statistics Cards -->
            <div class="row">
              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Attendance Rate</h6>
                        <h3 class="mb-0">{{ attendanceData.statistics.attendanceRate }}%</h3>
                        <small class="text-muted">
                          {{ attendanceData.statistics.presentDays + attendanceData.statistics.lateDays }}/{{ attendanceData.statistics.workingDays }} days
                        </small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-success-subtle text-success rounded-3">
                          <i class="mdi mdi-check-circle font-size-24"></i>
                        </span>
                      </div>
                    </div>
                    <div class="mt-2">
                      <div class="progress" style="height: 6px;">
                        <div class="progress-bar bg-success"
                             :style="{ width: attendanceData.statistics.attendanceRate + '%' }">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Total Hours</h6>
                        <h3 class="mb-0">{{ attendanceData.statistics.totalHours }}h</h3>
                        <small class="text-muted">
                          Avg: {{ attendanceData.statistics.avgHours }}h/day
                        </small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-primary-subtle text-primary rounded-3">
                          <i class="mdi mdi-clock-time-eight font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Late Arrivals</h6>
                        <h3 class="mb-0">{{ attendanceData.statistics.lateDays }}</h3>
                        <small class="text-muted">
                          {{ attendanceData.statistics.totalLateMinutes }} min total
                        </small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-warning-subtle text-warning rounded-3">
                          <i class="mdi mdi-clock-alert font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card card-stats">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h6 class="text-muted text-uppercase mb-2">Overtime</h6>
                        <h3 class="mb-0">{{ attendanceData.statistics.totalOvertime }}h</h3>
                        <small class="text-muted">Extra hours worked</small>
                      </div>
                      <div class="avatar-sm">
                        <span class="avatar-title bg-info-subtle text-info rounded-3">
                          <i class="mdi mdi-clock-plus font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Average Times & Monthly Breakdown -->
            <div class="row">
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-clock-outline text-primary me-2"></i>
                      Average Times
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <div class="d-flex justify-content-between align-items-center">
                        <span class="text-muted">Check In</span>
                        <h5 class="mb-0 text-success">{{ attendanceData.statistics.avgCheckIn || 'N/A' }}</h5>
                      </div>
                    </div>
                    <div>
                      <div class="d-flex justify-content-between align-items-center">
                        <span class="text-muted">Check Out</span>
                        <h5 class="mb-0 text-danger">{{ attendanceData.statistics.avgCheckOut || 'N/A' }}</h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-8">
                <div class="card">
                  <div class="card-header">
                    <h6 class="card-title mb-0">
                      <i class="mdi mdi-chart-line text-primary me-2"></i>
                      Last 6 Months Overview
                    </h6>
                  </div>
                  <div class="card-body">
                    <div v-if="attendanceData.monthlyBreakdown.length === 0" class="text-center text-muted py-3">
                      No data available
                    </div>
                    <div v-else class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Month</th>
                          <th class="text-center">Days</th>
                          <th class="text-center">Hours</th>
                          <th class="text-center">Late</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="month in attendanceData.monthlyBreakdown" :key="month.month">
                          <td>{{ formatMonth(month.month) }}</td>
                          <td class="text-center">{{ month.days }}</td>
                          <td class="text-center">{{ parseFloat(month.hours).toFixed(1) }}h</td>
                          <td class="text-center">
                            <span v-if="month.late_count > 0" class="badge bg-warning">{{ month.late_count }}</span>
                            <span v-else class="text-muted">-</span>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attendance Records Table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="mdi mdi-table text-primary me-2"></i>
                      Attendance Records
                    </h5>
                    <button @click="exportRecords" class="btn btn-outline-primary btn-sm">
                      <i class="mdi mdi-download me-1"></i>
                      Export
                    </button>
                  </div>
                  <div class="card-body">
                    <div v-if="attendanceData.attendanceRecords.length === 0" class="text-center py-5">
                      <div class="avatar-lg mx-auto mb-4">
                        <div class="avatar-title bg-light text-muted rounded-circle">
                          <i class="mdi mdi-calendar-blank font-size-48"></i>
                        </div>
                      </div>
                      <h5>No attendance records found</h5>
                      <p class="text-muted">Try adjusting your filters</p>
                    </div>

                    <div v-else class="table-responsive">
                      <table class="table table-hover">
                        <thead class="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Day</th>
                          <th>Check In</th>
                          <th>Check Out</th>
                          <th class="text-center">Hours</th>
                          <th class="text-center">Break</th>
                          <th class="text-center">Overtime</th>
                          <th class="text-center">Late</th>
                          <th>Status</th>
                          <th>Location</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="record in attendanceData.attendanceRecords" :key="record.id" @click="showRecordDetails(record)">
                          <td>
                            <strong>{{ formatDate(record.date) }}</strong>
                          </td>
                          <td>{{ getDayOfWeek(record.date) }}</td>
                          <td>
                              <span v-if="record.first_in" class="text-success">
                                <i class="mdi mdi-clock-in"></i> {{ record.first_in }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                              <span v-if="record.last_out" class="text-danger">
                                <i class="mdi mdi-clock-out"></i> {{ record.last_out }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ record.total_hours || '0' }}h</span>
                          </td>
                          <td class="text-center">
                            {{ record.break_hours || '0' }}h
                          </td>
                          <td class="text-center">
                              <span v-if="record.overtime_hours > 0" class="badge bg-info">
                                {{ record.overtime_hours }}h
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td class="text-center">
                              <span v-if="record.late_minutes > 0" class="badge bg-warning">
                                {{ record.late_minutes }}m
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td>
                              <span :class="getStatusBadgeClass(record.status)">
                                {{ record.status }}
                              </span>
                          </td>
                          <td>
                            <small class="text-muted">{{ record.location || 'N/A' }}</small>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="attendanceData.pagination.totalPages > 1" class="mt-3">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          Showing {{ ((attendanceData.pagination.currentPage - 1) * attendanceData.pagination.limit) + 1 }}
                          to {{ Math.min(attendanceData.pagination.currentPage * attendanceData.pagination.limit, attendanceData.pagination.totalRecords) }}
                          of {{ attendanceData.pagination.totalRecords }} records
                        </div>
                        <nav>
                          <ul class="pagination mb-0">
                            <li class="page-item" :class="{ disabled: attendanceData.pagination.currentPage === 1 }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(attendanceData.pagination.currentPage - 1)">
                                <i class="mdi mdi-chevron-left"></i>
                              </a>
                            </li>
                            <li v-for="page in visiblePages"
                                :key="page"
                                class="page-item"
                                :class="{ active: page === attendanceData.pagination.currentPage }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(page)">
                                {{ page }}
                              </a>
                            </li>
                            <li class="page-item" :class="{ disabled: attendanceData.pagination.currentPage === attendanceData.pagination.totalPages }">
                              <a class="page-link" href="javascript:void(0)" @click="changePage(attendanceData.pagination.currentPage + 1)">
                                <i class="mdi mdi-chevron-right"></i>
                              </a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>

    <!-- Record Details Modal -->
    <div class="modal fade" id="recordDetailsModal" tabindex="-1" ref="recordDetailsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" v-if="selectedRecord">
              <i class="mdi mdi-calendar-day me-2"></i>
              Attendance Details - {{ formatDateLong(selectedRecord.date) }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body" v-if="selectedRecord">
            <!-- Status Alert -->
            <div class="alert" :class="getStatusAlertClass(selectedRecord.status)">
              <h6 class="alert-heading">
                <i :class="getStatusIcon(selectedRecord.status)"></i>
                Status: {{ selectedRecord.status }}
              </h6>
            </div>

            <!-- Time Details -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-clock-outline me-1"></i>
                  Time Details
                </h6>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">First Check In</label>
                <p class="mb-0 fw-semibold">
                  <i class="mdi mdi-clock-in text-success me-1"></i>
                  {{ selectedRecord.first_in || 'N/A' }}
                </p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Last Check Out</label>
                <p class="mb-0 fw-semibold">
                  <i class="mdi mdi-clock-out text-danger me-1"></i>
                  {{ selectedRecord.last_out || 'N/A' }}
                </p>
              </div>
              <div class="col-md-4 mb-3">
                <label class="text-muted small">Total Hours</label>
                <p class="mb-0 fw-semibold">{{ selectedRecord.total_hours || '0' }} hours</p>
              </div>
              <div class="col-md-4 mb-3">
                <label class="text-muted small">Break Hours</label>
                <p class="mb-0 fw-semibold">{{ selectedRecord.break_hours || '0' }} hours</p>
              </div>
              <div class="col-md-4 mb-3">
                <label class="text-muted small">Overtime</label>
                <p class="mb-0 fw-semibold">{{ selectedRecord.overtime_hours || '0' }} hours</p>
              </div>
            </div>

            <!-- Performance Metrics -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-chart-line me-1"></i>
                  Performance Metrics
                </h6>
              </div>
              <div class="col-md-4 mb-3">
                <label class="text-muted small">Late Minutes</label>
                <p class="mb-0">
                  <span v-if="selectedRecord.late_minutes > 0" class="badge bg-warning">
                    {{ selectedRecord.late_minutes }} minutes
                  </span>
                  <span v-else class="text-success">On Time</span>
                </p>
              </div>
              <div class="col-md-4 mb-3">
                <label class="text-muted small">Early Departure</label>
                <p class="mb-0">
                  <span v-if="selectedRecord.early_departure_minutes > 0" class="badge bg-warning">
                    {{ selectedRecord.early_departure_minutes }} minutes
                  </span>
                  <span v-else class="text-success">None</span>
                </p>
              </div>
              <div class="col-md-4 mb-3">
                <label class="text-muted small">Longest Break</label>
                <p class="mb-0 fw-semibold">{{ selectedRecord.longest_break || '0' }} minutes</p>
              </div>
            </div>

            <!-- Activity Summary -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="text-primary mb-3">
                  <i class="mdi mdi-information-outline me-1"></i>
                  Activity Summary
                </h6>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Total Entries</label>
                <p class="mb-0 fw-semibold">{{ selectedRecord.total_entries || '0' }}</p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Total Exits</label>
                <p class="mb-0 fw-semibold">{{ selectedRecord.total_exits || '0' }}</p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Location</label>
                <p class="mb-0 fw-semibold">{{ selectedRecord.location || 'N/A' }}</p>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Day of Week</label>
                <p class="mb-0 fw-semibold">{{ getDayOfWeek(selectedRecord.date) }}</p>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeeAttendanceRecords",
  components: { NavigationBar },

  data() {
    return {
      loading: false,
      attendanceData: null,
      filters: {
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        status: ''
      },
      today: new Date().toISOString().split('T')[0],
      selectedRecord: null,
      recordDetailsModalInstance: null
    };
  },

  computed: {
    visiblePages() {
      if (!this.attendanceData) return [];

      const pages = [];
      const total = this.attendanceData.pagination.totalPages;
      const current = this.attendanceData.pagination.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    }
  },

  mounted() {
    this.loadAttendanceRecords();
    this.initRecordDetailsModal();
  },

  methods: {
    async loadAttendanceRecords() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeAttendanceRecords");
        data.append("startDate", this.filters.startDate);
        data.append("endDate", this.filters.endDate);
        data.append("status", this.filters.status);
        data.append("page", this.attendanceData?.pagination?.currentPage || 1);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.attendanceData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load attendance records');
        }
      } catch (error) {
        this.$toast?.error("Failed to load attendance records: " + error.message) ||
        console.error("Failed to load attendance records:", error);
      } finally {
        this.loading = false;
      }
    },

    initRecordDetailsModal() {
      const modalElement = this.$refs.recordDetailsModal;
      if (modalElement && typeof bootstrap !== 'undefined') {
        this.recordDetailsModalInstance = new bootstrap.Modal(modalElement);
      }
    },

    showRecordDetails(record) {
      this.selectedRecord = record;
      if (this.recordDetailsModalInstance) {
        this.recordDetailsModalInstance.show();
      }
    },

    changePage(page) {
      if (page < 1 || page > this.attendanceData.pagination.totalPages || page === this.attendanceData.pagination.currentPage || page === '...') {
        return;
      }
      this.attendanceData.pagination.currentPage = page;
      this.loadAttendanceRecords();
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatDateLong(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatMonth(monthStr) {
      if (!monthStr) return 'N/A';
      const [year, month] = monthStr.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    },

    getDayOfWeek(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { weekday: 'short' });
    },

    getStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'present') return 'badge bg-success';
      if (statusLower === 'late') return 'badge bg-warning';
      if (statusLower === 'partial') return 'badge bg-secondary';
      if (statusLower === 'absent') return 'badge bg-danger';
      if (statusLower === 'overtime') return 'badge bg-info';
      return 'badge bg-secondary';
    },

    getStatusAlertClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'present') return 'alert-success';
      if (statusLower === 'late') return 'alert-warning';
      if (statusLower === 'partial') return 'alert-secondary';
      if (statusLower === 'absent') return 'alert-danger';
      if (statusLower === 'overtime') return 'alert-info';
      return 'alert-info';
    },

    getStatusIcon(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'present') return 'mdi mdi-check-circle';
      if (statusLower === 'late') return 'mdi mdi-clock-alert';
      if (statusLower === 'partial') return 'mdi mdi-clock-outline';
      if (statusLower === 'absent') return 'mdi mdi-close-circle';
      if (statusLower === 'overtime') return 'mdi mdi-clock-plus';
      return 'mdi mdi-information';
    },

    exportRecords() {
      // TODO: Implement export functionality
      this.$toast?.info("Export functionality coming soon") ||
      alert("Export functionality coming soon");
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
.card-stats {
  transition: all 0.3s ease;
}

.card-stats:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.avatar-sm {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-lg {
  width: 72px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1);
}

.bg-primary-subtle {
  background-color: rgba(91, 115, 232, 0.1);
}

.table tbody tr {
  cursor: pointer;
}

.table tbody tr:hover {
  background-color: rgba(91, 115, 232, 0.05);
}

.pagination .page-link {
  color: #5b73e8;
}

.pagination .page-item.active .page-link {
  background-color: #5b73e8;
  border-color: #5b73e8;
}

@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    font-size: 0.875rem;
  }
}
</style>